import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo } from 'react';
import {
  DeepMap,
  FieldError,
  Resolver,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  MAX_OTHER_RECIPIENTS,
  OTHER_RECIPIENTS_EMAIL_REGEX,
  OtherRecipientsInput,
  parseOtherRecipients,
} from './otherRecipients';

export enum FIELDS {
  RECIPIENTS = 'recipients',
  SEND_TO_ME = 'sendCopy',
  OTHER_RECIPIENTS = 'otherRecipients',
  SUBJECT = 'subject',
  MESSAGE = 'message',
  ATTACHMENTS = 'attachments',
}

/** Total size limit for all email attachments (15 MiB). */
export const MAX_EMAIL_ATTACHMENTS_BYTES = 15 * 1024 * 1024;

export type EmailModuleFormValues = {
  [FIELDS.RECIPIENTS]: string[];
  [FIELDS.SEND_TO_ME]: boolean;
  [FIELDS.OTHER_RECIPIENTS]: string;
  [FIELDS.SUBJECT]: string;
  [FIELDS.MESSAGE]: string;
  [FIELDS.ATTACHMENTS]: any[];
};

export const getFieldError = (
  errors: DeepMap<EmailModuleFormValues, FieldError>,
  field: FIELDS,
): FieldError | undefined => {
  const error = errors[field];
  if (!error) return undefined;
  if (Array.isArray(error)) {
    return error.find((e) => e?.message) ?? error[0];
  }
  return error;
};

export const useEmailModuleForm = (defaultValues: EmailModuleFormValues) => {
  const { t } = useTranslation();

  const resolver: Resolver<EmailModuleFormValues> = useMemo(
    () =>
      yupResolver(
        yup.object().shape({
          [FIELDS.SEND_TO_ME]: yup.bool(),
          [FIELDS.RECIPIENTS]: yup
            .array()
            .of(yup.string())
            .min(1, t('errors.recipientsRequired')),
          [FIELDS.OTHER_RECIPIENTS]: yup
            .array()
            .transform(function (value, originalValue) {
              if (this.isType(value) && value !== null) {
                return value;
              }
              return parseOtherRecipients(
                originalValue as OtherRecipientsInput,
              );
            })
            .notRequired()
            .nullable()
            .default([])
            .max(
              MAX_OTHER_RECIPIENTS,
              t('email.otherRecipientsMax', { max: MAX_OTHER_RECIPIENTS }),
            )
            .of(
              yup
                .string()
                .email(({ value }) =>
                  t('email.otherRecipientsInvalid', { email: value }),
                )
                .matches(OTHER_RECIPIENTS_EMAIL_REGEX, {
                  excludeEmptyString: true,
                  message: ({ value }) =>
                    t('email.otherRecipientsInvalid', { email: value }),
                }),
            ),
          [FIELDS.SUBJECT]: yup.string().required(t('errors.required')),
          [FIELDS.MESSAGE]: yup.string().required(t('errors.required')),
          [FIELDS.ATTACHMENTS]: yup
            .array()
            .test(
              'max-total-size',
              t('email.maxAttachmentsSize'),
              (files: unknown) => {
                if (!Array.isArray(files) || files.length === 0) return true;
                const total = files.reduce(
                  (sum: number, f: { size?: number }) => sum + (f?.size ?? 0),
                  0,
                );
                return total <= MAX_EMAIL_ATTACHMENTS_BYTES;
              },
            ),
        }),
      ),
    [t],
  );

  const form = useForm<EmailModuleFormValues>({
    resolver,
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { field: attachmentsFieldProps } = useController({
    name: FIELDS.ATTACHMENTS,
    control: form.control,
    defaultValue: defaultValues[FIELDS.ATTACHMENTS],
  });

  const { field: recipientsFieldProps } = useController({
    name: FIELDS.RECIPIENTS,
    control: form.control,
    defaultValue: defaultValues[FIELDS.RECIPIENTS],
  });

  const { field: sendToMeFieldProps } = useController({
    name: FIELDS.SEND_TO_ME,
    control: form.control,
    defaultValue: defaultValues[FIELDS.SEND_TO_ME],
  });

  const { field: otherRecipientsFieldProps } = useController({
    name: FIELDS.OTHER_RECIPIENTS,
    control: form.control,
    defaultValue: defaultValues[FIELDS.OTHER_RECIPIENTS],
  });

  const values = useWatch({
    name: [
      FIELDS.SEND_TO_ME,
      FIELDS.RECIPIENTS,
      FIELDS.OTHER_RECIPIENTS,
      FIELDS.SUBJECT,
      FIELDS.MESSAGE,
      FIELDS.ATTACHMENTS,
    ],
    control: form.control,
    defaultValue: defaultValues,
  });

  return {
    ...form,
    values,
    attachmentsFieldProps,
    recipientsFieldProps,
    sendToMeFieldProps,
    otherRecipientsFieldProps,
  };
};
